// Pay-period maths for the UK Payslip generator. UK pay runs weekly,
// fortnightly, four-weekly or (most often) monthly; the first three are a
// fixed number of days, a month is a calendar month. The page, the download
// generator and the preview generator all step through periods with these.

export const UK_PAY_FREQUENCIES = [
  ["weekly", "Weekly"],
  ["biweekly", "Fortnightly"],
  ["fourweekly", "Four-weekly"],
  ["monthly", "Monthly"],
];

const PERIODS = { weekly: 52, biweekly: 26, fourweekly: 13, monthly: 12 };
const DAYS = { weekly: 7, biweekly: 14, fourweekly: 28 };
const HOURS = { weekly: 37.5, biweekly: 75, fourweekly: 150, monthly: 162.5 }; // a 37.5-hour week

export const periodsPerYear = (frequency) => PERIODS[frequency] || 12;
// Days in a fixed-length period; null for a calendar month
export const periodDays = (frequency) => DAYS[frequency] || null;
export const defaultHours = (frequency) => HOURS[frequency] || HOURS.monthly;

// The last day of the period that starts on `start`
export function periodEnd(start, frequency) {
  const d = new Date(start);
  const n = periodDays(frequency);
  if (n) d.setDate(d.getDate() + n - 1);
  else { d.setMonth(d.getMonth() + 1); d.setDate(d.getDate() - 1); }
  return d;
}

// `start` moved forward by `count` periods
export function addPeriods(start, frequency, count) {
  const d = new Date(start);
  const n = periodDays(frequency);
  if (n) d.setDate(d.getDate() + n * count);
  else d.setMonth(d.getMonth() + count);
  return d;
}

// How many periods a date range covers (the number of payslips to make)
export function countPeriods(start, end, frequency) {
  if (!(start instanceof Date) || !(end instanceof Date) || isNaN(start) || isNaN(end)) return 0;
  const n = periodDays(frequency);
  const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24));
  if (n) return Math.ceil(diffDays / n);
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() >= start.getDate()) months += 1;
  return Math.max(1, months);
}

// The UK tax year runs 6 April to 5 April: the start of the tax year a date
// falls in (year-to-date figures on a payslip count from here)
export function taxYearStart(date) {
  const d = new Date(date);
  const inNewYear = d.getMonth() > 3 || (d.getMonth() === 3 && d.getDate() >= 6);
  return new Date(inNewYear ? d.getFullYear() : d.getFullYear() - 1, 3, 6);
}

// HMRC tax months run from the 6th to the 5th and tax weeks count from 6
// April. The tax period a payslip belongs to, by its pay date: "Month 10"
// or "Week 43", with the tax year it falls in ("2024/25").
export function taxPeriod(date, frequency) {
  const d = new Date(date);
  const start = taxYearStart(d);
  const y1 = start.getFullYear();
  const taxYear = `${y1}/${String(y1 + 1).slice(-2)}`;
  if (frequency === "monthly") {
    const n = (d.getFullYear() - y1) * 12 + (d.getMonth() - 3) + (d.getDate() >= 6 ? 1 : 0);
    const month = Math.max(1, Math.min(12, n));
    return { label: `Month ${month}`, number: month, taxYear };
  }
  const week = Math.max(1, Math.min(53, Math.floor((d.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1));
  return { label: `Week ${week}`, number: week, taxYear };
}

// Periods paid so far this tax year: from the later of the hire date and
// 6 April, through the period that ends on `end`
export function ytdPeriods(hireDate, end, frequency) {
  const startOfYear = taxYearStart(end);
  const from = hireDate && hireDate > startOfYear ? new Date(hireDate) : startOfYear;
  const n = periodDays(frequency);
  if (n) {
    const diffDays = Math.ceil((end.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(1, Math.ceil(diffDays / n));
  }
  return Math.max(1, (end.getFullYear() - from.getFullYear()) * 12 + end.getMonth() - from.getMonth() + 1);
}
